import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { motion } from "framer-motion";
import { MapPin, Clock, Zap } from "lucide-react";
import { ServiceAreaMap } from "@/components/MapComponents";

export default function ServiceAreaSection() {
  const { t } = useLanguage();

  const stats = [
    { icon: Zap, label: t.serviceArea.response, value: t.serviceArea.responseTime },
    { icon: Clock, label: t.serviceArea.available, value: t.serviceArea.availableTime },
    { icon: MapPin, label: t.serviceArea.coverage, value: t.serviceArea.coverageValue },
  ];

  return (
    <section id="service-area" data-testid="service-area-section" className="py-24 md:py-32 bg-[#141311]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden border border-white/5 h-[350px] lg:h-[450px] glow-gold" data-testid="service-area-map">
              <ServiceAreaMap />
            </div>
            <div className="mt-4 bg-[#1C1B18] border border-white/5 rounded-2xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-[#BF953F] via-[#F9E58A] to-[#B38728] rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-black" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">{t.serviceArea.radius}</p>
                <p className="text-xs text-[#8A8881]">Houston, TX</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#D4AF37]">
              {t.serviceArea.badge}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-white mt-3 mb-4">
              {t.serviceArea.title}
            </h2>
            <p className="text-base text-[#D1D0CC] leading-relaxed mb-4">
              {t.serviceArea.subtitle}
            </p>
            <p className="text-sm text-[#8A8881] leading-relaxed mb-10">
              {t.serviceArea.cities}
            </p>

            <div className="grid grid-cols-3 gap-4">
              {stats.map((stat, i) => (
                <div
                  key={i}
                  data-testid={`stat-${i}`}
                  className="bg-[#1C1B18] border border-white/5 rounded-2xl p-5 text-center hover:border-[#D4AF37]/20 transition-colors duration-300"
                >
                  <stat.icon className="w-5 h-5 text-[#D4AF37] mx-auto mb-3" />
                  <p className="text-xl lg:text-2xl font-heading font-bold text-gold-gradient">{stat.value}</p>
                  <p className="text-xs text-[#8A8881] mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
